import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';
import { under2camel, makeHeadData } from 'lib/commonLib';

import { getMediaTargetParAdut } from 'lib/api/media';
import CurrentStatus from 'components/atoms/CurrentStatus';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import Highchart from 'components/organisms/Chart/Highchart';
import SwitchingArea from 'components/organisms/SwitchingArea';
import NewTableContainer from 'components/molecules/NewTable';

const TargetParAdutGraph = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    params,
    handleClickSnackbar = (f) => f,
    infoMessage = t('까지의 통계 그래프입니다.', {
      date: t('term', {
        startDate: moment(params.sDate).format('YYYY-MM-DD'),
        endDate: moment(params.eDate).format('YYYY-MM-DD'),
      }),
    }),
    title = '정산방식별',
    reFetch,
  } = props;

  const graphDefaultOption = {
    colors: [
      '#FF0000',
      '#FFA3A5',
      '#90ed7d',
      '#f7a35c',
      '#8085e9',
      '#2F2FFF',
      '#e4d354',
      '#659b60',
      '#8d4653',
      '#91e8e1',
      '#F361DC',
      '#4641D9',
      '#8C8C8C',
    ],
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: 'pie',
      backgroundColor: theme.palette.common.bgColor,
    },
    title: { text: t('지출금액'), style: { fontWeight: 'bold' } },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f} %',
        },
        size: 200,
      },
    },
    tooltip: {
      headerFormat: '',
      pointFormat:
        '<b>{point.subName} : </b> {point.percentage:.1f} %<br /><b>{series.name} : </b> {point.y}',
    },
    series: [{ name: '지출금액', colorByPoint: true, data: [] }],
    //하단 로고 삭제
    credits: { enabled: false },
  };

  const initOptions = {
    advrtsAmt: graphDefaultOption,
    noParLossAmt: graphDefaultOption,
  };

  const [status, setStatus] = useState('loading');
  const [optionList, setOptionList] = useState(initOptions);
  const [prevParams, setPrevParams] = useState({});

  const tableHead = [
    {
      'id': 'RANK',
      'label': t(''),
      'orderFlag': false,
      'type': 'string',
      'format': '@',
      'displayFlag': true,
      'calFlag': false,
    },
    {
      'id': 'CATEGORY',
      'label': t('분류'),
      'orderFlag': false,
      'type': 'string',
      'format': '@',
      'displayFlag': true,
      'calFlag': false,
    },
    {
      'id': 'DAMAGE',
      'label': t('손해유무'),
      'orderFlag': false,
      'type': 'string',
      'format': '@',
      'displayFlag': true,
      'calFlag': false,
    },
    {
      'id': 'DESCRIPTION',
      'label': t('설명'),
      'orderFlag': false,
      'type': 'string',
      'format': '@',
      'displayFlag': true,
      'calFlag': false,
    },
  ];

  const tableList = [
    {
      RANK: t('1등급'),
      CATEGORY: t('CPP (개런티)'),
      DAMAGE: t('손해O'),
      DESCRIPTION: t('GT지면'),
    },
    {
      RANK: t('2등급'),
      CATEGORY: t('CPM 매체 기준 정산'),
      DAMAGE: t('손해O'),
      DESCRIPTION: t('패스백 X (ex 매일경제,한겨레)'),
    },
    {
      RANK: t('3등급'),
      CATEGORY: t('CPC 주요매체'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백 X, 직매체'),
    },
    {
      RANK: t('4등급'),
      CATEGORY: t('RS 주요매체'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백 X, 직매체'),
    },
    {
      RANK: t('5등급'),
      CATEGORY: t('CPM + F보장'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백 X, 직매체+신디+커뮤니티'),
    },
    {
      RANK: t('6등급'),
      CATEGORY: t('CPC + F보장'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백 X, 신디+커뮤니티'),
    },
    {
      RANK: t('7등급'),
      CATEGORY: t('RS + F보장'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백 X, 신디+커뮤니티'),
    },
    {
      RANK: t('8등급'),
      CATEGORY: t('RS + F비보장'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백O'),
    },
    {
      RANK: t('9등급'),
      CATEGORY: t('CPC + F비보장'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백O'),
    },
    {
      RANK: t('10등급'),
      CATEGORY: t('CPM + F비보장'),
      DAMAGE: t('손해X'),
      DESCRIPTION: t('패스백O'),
    },
    {
      RANK: t('11등급'),
      CATEGORY: t('Open RTB'),
      DAMAGE: t('손해X'),
      DESCRIPTION: '',
    },
    {
      RANK: t('12등급'),
      CATEGORY: t('서비스'),
      DAMAGE: t('손해X'),
      DESCRIPTION: '',
    },
  ];

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(prevParams) || reFetch) {
      getGraphList();
    }
    // eslint-disable-next-line
  }, [params, reFetch]);

  const getGraphList = () => {
    setStatus('loading');
    getMediaTargetParAdut({ params: params })
      .then((response) => {
        const { data } = response.data;
        let newOptions = initOptions;
        for (let key in data) {
          if (key !== 'ACCOUNT_STR' && key !== 'RANK') {
            let newData = [];
            for (let item of data[key]['data']) {
              newData.push({
                name: getPieSubTitle(item['name']),
                subName: getPieSubTitle(item['name']),
                y: item['val'],
              });
            }
            newOptions[under2camel(key)] = {
              ...newOptions[under2camel(key)],
              title: {
                ...newOptions[under2camel(key)]['title'],
                text: data[key]['title'],
              },
              series: [
                {
                  name: data[key]['title'],
                  id: under2camel(key),
                  data: newData,
                },
              ],
            };
          }
        }
        setStatus('');
        setOptionList(newOptions);
      })
      .catch((error) => {
        handleClickSnackbar({ message: error, option: { type: 'error' } });
        setStatus('error');
      });
    setPrevParams(params);
  };

  const getPieSubTitle = (title) => {
    let result = '';
    switch (title) {
      case '1':
        result = t('1. CPP (개런티)');
        break;
      case '2':
        result = t('2. CPM 매체 기준 정산');
        break;
      case '3':
        result = t('3. CPC 주요매체');
        break;
      case '4':
        result = t('4. RS 주요매체');
        break;
      case '5':
        result = t('5. CPM + F보장');
        break;
      case '6':
        result = t('6. CPC + F보장');
        break;
      case '7':
        result = t('7. RS + F보장');
        break;
      case '8':
        result = t('8. RS + F비보장');
        break;
      case '9':
        result = t('9. CPC + F비보장');
        break;
      case '10':
        result = t('10. CPM + F비보장');
        break;
      case '11':
        result = t('11. Open RTB');
        break;
      case '12':
        result = t('12. 서비스');
        break;
      default:
        result = title;
    }
    return result;
  };

  const customized = {
    DESCRIPTION: { headStyle: { width: 200 } },
  };
  return (
    <SwitchingArea
      title={
        <InfoTitleArea
          title={title}
          tooltipTitle={infoMessage}
          iconType="fainfoCircle"
          iconColor="graph"
        />
      }
      isOpen={false}
    >
      {status !== '' ? (
        <CurrentStatus type={status} />
      ) : (
        <ContentsWrap contentsCount={3}>
          <Highchart options={optionList['advrtsAmt']} />
          <Highchart options={optionList['noParLossAmt']} />
          <NewTableContainer
            params={{}}
            getParam={(f) => f}
            // params 값이 없이 사용하는 테이블
            useCustomTable
            propsList={tableList}
            propsHeadCells={makeHeadData(tableHead)}
            loading={false}
            useStoreLoading={false}
            useFetch={false}
            useTotal={false}
            usePagenation={false}
            useToolBar={false}
            rowsPerPage={-1}
            customized={customized}
          />
        </ContentsWrap>
      )}
    </SwitchingArea>
  );
};

const isEqual = (prefProps, nextProps) => {
  let equal = JSON.stringify(prefProps) !== JSON.stringify(nextProps);
  return !equal;
};

export default React.memo(TargetParAdutGraph, isEqual);
